// @givmo/design-system — RadioTile + RadioTileGroup: a labelled radio option, at the SaaS control
// scale (Org Machinery OM-17, Gates 2a — D-CPO-38 (3), Jonathan 2026-09-06: "the UI design here
// looks terrible with the radio tiles so large, so actually this needs to be fixed everywhere they
// are used").
//
// Built on Base UI's Radio + RadioGroup (@base-ui/react). Every rendering site is a SaaS surface — a
// dialog or a settings tab in the consoles and the employer portal — so the option reads at the scale
// its neighbours wear: the label at `text-s14` (the dialogs' ledes, the Select's value, the button
// label), semibold when selected and medium otherwise; the description at `text-s13` in the tertiary
// tone (a Card subtitle); an 18px radio (the SaaS Checkbox's size) on the brand tone. It was born a
// port of the Flutter consumer `RadioTile` (a 22px Lato label, a 20px radio, a 20px gap) and rendered
// too large on every one of those surfaces.
//
// ⚠ THE LABEL'S LEFT OFFSET IS 30px (an 18px radio + `ml-12`). One usage site draws its own
// description OUTSIDE the tile, indented to the label's edge (`ml-[30px]`,
// `packages/employer-module/src/features/settings/settings-privacy-tab.tsx` — aligned to this geometry under the
// D-CPO-38 rider of 2026-09-06, the ONE site change the compaction carried); any future external
// description aligns to the same 30px, and a change to the radio size or the gap here moves it.
//
// ⚠ A PRESENTATION CHANGE ONLY. The markup, the `testId` → `Radio.Root` seam (the e2e ids sit on it),
// keyboard and focus, and the checked/unchecked states are exactly as before.
//
// `RadioTileGroup` wraps Base UI's RadioGroup (controlled `value` / `onValueChange`); each
// `RadioTile` is one labelled option. The option row keeps the 44px phone tap floor (OM-13) and
// unwinds it at `lg`.
import { createContext, useContext, useId } from 'react';
import { Radio } from '@base-ui/react/radio';
import { RadioGroup } from '@base-ui/react/radio-group';
import { cn } from '../lib/utils';
import { testIdProp, type TestIdProps } from '../lib/testid';

interface RadioTileGroupContextValue {
  value: string | undefined;
  disabled: boolean;
}
const RadioTileGroupContext = createContext<RadioTileGroupContextValue>({
  value: undefined,
  disabled: false,
});

/** The label's type: the SaaS control scale, semibold when selected. Exported for the pin. */
export const RADIO_TILE_LABEL_CLASS = 'font-sans text-s14 tracking-givmo text-text-strong';
export const RADIO_TILE_LABEL_SELECTED_WEIGHT = 'font-w600';
export const RADIO_TILE_LABEL_UNSELECTED_WEIGHT = 'font-w500';
/** The description's type: a Card subtitle's. */
export const RADIO_TILE_DESCRIPTION_CLASS =
  'mt-2 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary';
/**
 * The description in its `secondary` tone (`descriptionTone="secondary"`): the same type in the
 * secondary text colour, for a host that tints its rows (selected or hovered). The tertiary tone falls
 * under AA's 4.5:1 on those tints (`src/a11y/token-contrast.test.ts`'s known gaps); the secondary one
 * holds it on every surface. Spelled out, never merged through `cn`: tailwind-merge reads `font-w400`
 * as a font family and would drop `font-sans`.
 */
export const RADIO_TILE_DESCRIPTION_SECONDARY_CLASS =
  'mt-2 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary';
/** The radio's size — the SaaS Checkbox's 18px. */
export const RADIO_TILE_RADIO_SIZE_CLASS = 'size-18';
/** The gap between the radio and the label — with the 18px radio, the label's left offset is 30px (see the header). */
export const RADIO_TILE_LABEL_GAP_CLASS = 'ml-12';
/** The option row's phone tap floor, unwound at `lg` (OM-13). */
export const RADIO_TILE_ROW_FLOOR_CLASS = 'min-h-44 lg:min-h-0';

export interface RadioTileGroupProps extends TestIdProps {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  name?: string;
  disabled?: boolean;
  children: React.ReactNode;
  className?: string;
  'aria-label'?: string;
}

export function RadioTileGroup({
  value,
  defaultValue,
  onValueChange,
  name,
  disabled,
  children,
  className,
  testId,
  'aria-label': ariaLabel,
}: RadioTileGroupProps) {
  return (
    <RadioTileGroupContext.Provider
      value={{ value: value ?? defaultValue, disabled: disabled ?? false }}
    >
      <RadioGroup
        value={value}
        defaultValue={defaultValue}
        onValueChange={(next) => onValueChange?.(String(next))}
        name={name}
        disabled={disabled}
        aria-label={ariaLabel}
        className={cn('flex flex-col gap-8', className)}
        {...testIdProp(testId)}
      >
        {children}
      </RadioGroup>
    </RadioTileGroupContext.Provider>
  );
}

export interface RadioTileProps extends TestIdProps {
  /** This option's value (must match the group's value to be selected). */
  value: string;
  /** Label text (the SaaS control scale; semibold when selected). */
  label: string;
  /**
   * Optional secondary description line under the label (a Card subtitle's type). When set, the
   * row is top-aligned so the radio lines up with the first line of text.
   */
  description?: string;
  /**
   * The description's tone: `tertiary` (a Card subtitle's, the default) or `secondary`, for a host
   * that tints its rows — selected or hovered — where the tertiary tone falls under AA's 4.5:1.
   */
  descriptionTone?: 'tertiary' | 'secondary';
  disabled?: boolean;
  className?: string;
}

export function RadioTile({
  value,
  label,
  description,
  descriptionTone = 'tertiary',
  disabled,
  className,
  testId,
}: RadioTileProps) {
  const { value: groupValue, disabled: groupDisabled } = useContext(RadioTileGroupContext);
  const selected = groupValue === value;
  // A disabled GROUP disables every radio in it (Base UI), so the row's cursor follows either.
  const unavailable = disabled === true || groupDisabled;
  const labelId = useId();

  return (
    <label
      htmlFor={labelId}
      className={cn(
        'inline-flex',
        RADIO_TILE_ROW_FLOOR_CLASS,
        description ? 'items-start' : 'items-center',
        unavailable ? 'cursor-not-allowed' : 'cursor-pointer',
        className,
      )}
    >
      <Radio.Root
        id={labelId}
        value={value}
        disabled={disabled}
        className={cn(
          'flex shrink-0 items-center justify-center rounded-all100 border-2 bg-surface transition-colors',
          RADIO_TILE_RADIO_SIZE_CLASS,
          // Keep the radio aligned to the first text line when a description wraps below.
          description && 'mt-[1px]',
          // Unselected ring = the strong border; selected ring = the brand tone.
          'border-neutral-400 data-[checked]:border-brand-600',
          'hover:border-neutral-500 data-[checked]:hover:border-brand-600',
          'outline-none focus-visible:shadow-ring',
          // Keyed on Base UI's `data-disabled`: the root is a `<span role="radio">`, which the
          // `:disabled` pseudo-class (the `disabled:` variant) never matches.
          'data-[disabled]:opacity-50',
        )}
        {...testIdProp(testId)}
      >
        <Radio.Indicator className="size-8 rounded-all100 bg-brand-600 data-[unchecked]:hidden" />
      </Radio.Root>
      <span className={cn(RADIO_TILE_LABEL_GAP_CLASS, 'mr-8 flex min-w-0 flex-col')}>
        <span
          className={cn(
            RADIO_TILE_LABEL_CLASS,
            selected ? RADIO_TILE_LABEL_SELECTED_WEIGHT : RADIO_TILE_LABEL_UNSELECTED_WEIGHT,
          )}
        >
          {label}
        </span>
        {description && (
          <span
            className={
              descriptionTone === 'secondary'
                ? RADIO_TILE_DESCRIPTION_SECONDARY_CLASS
                : RADIO_TILE_DESCRIPTION_CLASS
            }
          >
            {description}
          </span>
        )}
      </span>
    </label>
  );
}
